import { reactive } from 'vue'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import type { AuthState } from '@/types/authState'
import { normalizeError } from '@/util/errorNormalizer'
import type {
  SlackSubscriptionResponse,
  SlackTestNotificationResponse,
} from '@/types/slackNotifications'

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COMPLETE = 'COMPLETE',
}

interface SlackNotificationApi {
  state: API_STATES
  getSubscription(authState: AuthState): Promise<SlackSubscriptionResponse | null>
  subscribe(authState: AuthState): Promise<SlackSubscriptionResponse>
  unsubscribe(authState: AuthState): Promise<void>
  sendTestNotification(authState: AuthState): Promise<SlackTestNotificationResponse>
}

export const slackNotificationApi: SlackNotificationApi = reactive({
  state: API_STATES.INITIAL,

  async getSubscription(
    authState: AuthState,
  ): Promise<SlackSubscriptionResponse | null> {
    slackNotificationApi.state = API_STATES.LOADING
    const token = authState.user?.accessToken
    const url = API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.SLACK_NOTIFICATIONS.SUBSCRIPTION

    return axios
      .get<SlackSubscriptionResponse>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        slackNotificationApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        slackNotificationApi.state = API_STATES.COMPLETE
        // 404 means not subscribed - return null
        if (err.response?.status === 404) {
          return null
        }
        slackNotificationApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async subscribe(authState: AuthState): Promise<SlackSubscriptionResponse> {
    slackNotificationApi.state = API_STATES.LOADING
    const token = authState.user?.accessToken
    const url = API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.SLACK_NOTIFICATIONS.SUBSCRIBE

    return axios
      .post<SlackSubscriptionResponse>(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(({ data }) => {
        slackNotificationApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        slackNotificationApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async unsubscribe(authState: AuthState): Promise<void> {
    slackNotificationApi.state = API_STATES.LOADING
    const token = authState.user?.accessToken
    const url = API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.SLACK_NOTIFICATIONS.UNSUBSCRIBE

    return axios
      .delete(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        slackNotificationApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        slackNotificationApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  async sendTestNotification(
    authState: AuthState,
  ): Promise<SlackTestNotificationResponse> {
    slackNotificationApi.state = API_STATES.LOADING
    const token = authState.user?.accessToken
    const url = API_ENDPOINTS.SHOPIFY_PAYOUTS_EMBEDDED.SLACK_NOTIFICATIONS.TEST

    return axios
      .post<SlackTestNotificationResponse>(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(({ data }) => {
        slackNotificationApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        slackNotificationApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
})
